import { EmptyState as CrowEmptyState, LoadingState as CrowLoadingState } from '@crow/ui/feedback';
import type { ComponentProps, ReactNode } from 'react';
import { TONES, type Tone } from '../../lib/tones';
import { cn } from '../../lib/utils';

export type InlineMessageTone = Tone;

/**
 * The one notice primitive: a rounded card with a tone icon, an optional short
 * `title` (what happened) followed by the detail, and optional `actions`.
 * Errors are announced as alerts, everything else as a status.
 */
export function InlineMessage({ tone = 'info', title, actions, compact = false, className, children, ...props }:
  Omit<ComponentProps<'div'>, 'title'> & {
    tone?: InlineMessageTone;
    title?: ReactNode;
    actions?: ReactNode;
    compact?: boolean;
  }) {
  const { icon: Icon, card, mark } = TONES[tone];
  const hasDetail = children !== undefined && children !== null && children !== false;
  return <div data-slot="inline-message" role={tone === 'error' ? 'alert' : 'status'}
    className={cn('flex flex-wrap items-start gap-x-2.5 gap-y-2 rounded-xl border',
      compact ? 'type-caption px-3 py-2' : 'type-ui px-3.5 py-2.5', card, className)} {...props}>
    <Icon aria-hidden="true" className={cn('mt-0.5 shrink-0', compact ? 'size-3.5' : 'size-4', mark)} />
    <p className="min-w-0 flex-1 break-words leading-5">
      {title !== undefined && <span className="font-medium text-foreground">{title}</span>}
      {title !== undefined && hasDetail && ' '}
      {hasDetail && <span className={title === undefined ? 'text-foreground/90' : 'text-muted-foreground'}>{children}</span>}
    </p>
    {actions !== undefined && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
  </div>;
}

/** A view still loading: the design system's (`design-system/ui`) spinner and what it waits for. */
export function LoadingState({ children = 'Loading…', className }: { children?: ReactNode; className?: string }) {
  return <CrowLoadingState className={className}>{children}</CrowLoadingState>;
}

/** The design system's empty state (`design-system/ui`), centred in the panel that frames it. */
export function EmptyState({ icon, title, description, action, className }: {
  icon?: ReactNode; title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string;
}) {
  return <CrowEmptyState variant="plain" icon={icon} title={title} action={action}
    className={cn('p-panel', className)}>{description}</CrowEmptyState>;
}
